/**
 * QrScanSheet — iter119.
 *
 * Payer-side QR scanner. Opens the device camera, decodes a SplitEasy
 * settlement QR (see QrSettleSheet for payload shape), shows a confirmation
 * screen with the parsed amount + recipient, and on confirm POSTs to the
 * existing /coin-transfers/qr-settle endpoint.
 *
 * Designed as a self-contained sheet — drop it anywhere with a trigger button.
 * Uses html5-qrcode for camera access (Chrome / Safari on mobile; falls back
 * to "no camera" message gracefully).
 *
 * States:
 *   - 'scanning' → camera open, looking for a code
 *   - 'review'   → decoded; show payload + amount input (if QR was amount-less)
 *   - 'sending'  → POST in flight
 *   - 'error'    → camera unavailable or QR rejected
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Camera, X, Check, ArrowRight, AlertTriangle, Users } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import api from '../lib/api';
import { formatNXC } from '../lib/formatNXC';
import { parseQrText } from '../lib/qrPayload';
import UpiPayButton from './UpiPayButton';

const SCANNER_ID = 'qr-scan-region';

function parsePayload(raw) {
  // iter125 — accepts URL form (https://split-easy.in/s/<b64>) AND legacy raw JSON.
  const obj = parseQrText(raw);
  if (!obj) return null;
  // iter119 — per-debt QR carries group_id + amount
  if (obj.kind === 'spliteasy_settle' && obj.to_user_id && obj.group_id) {
    return { ...obj, _kind: 'settle' };
  }
  // iter120 — personal QR carries only user identity
  if (obj.kind === 'spliteasy_user' && obj.user_id) {
    return { ...obj, _kind: 'user' };
  }
  return null;
}

export default function QrScanSheet({ open, onOpenChange, onSettled }) {
  const [state, setState] = useState('scanning'); // scanning | review | sending | error
  const [decoded, setDecoded] = useState(null);
  const [amountInput, setAmountInput] = useState('');
  const [error, setError] = useState(null);
  const [sharedGroups, setSharedGroups] = useState([]); // groups both me + scanned user are in
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [payeeUpi, setPayeeUpi] = useState(null); // iter235 — payee's saved UPI ID
  const html5QrRef = useRef(null);

  // Cleanup helper — stops any running camera stream.
  const stopScanner = useCallback(async () => {
    const inst = html5QrRef.current;
    html5QrRef.current = null;
    if (!inst) return;
    try {
      if (inst.isScanning) await inst.stop();
    } catch { /* ignore */ }
    try { await inst.clear(); } catch { /* ignore */ }
  }, []);

  // Reset state on close.
  useEffect(() => {
    if (!open) {
      stopScanner();
      setState('scanning');
      setDecoded(null);
      setAmountInput('');
      setError(null);
      setSharedGroups([]);
      setSelectedGroupId('');
      setPayeeUpi(null);
    }
  }, [open, stopScanner]);

  // When we decode a personal QR, fetch the groups we share with that user.
  const loadSharedGroups = useCallback(async (otherUserId) => {
    try {
      // iter121 — call the new /dashboard/debts-with/{user_id} endpoint which
      // returns shared groups annotated with owe/owed amounts. Lets us pre-fill
      // amount chips ("Pickle 1,500", "Volleyball 800") instead of forcing the
      // payer to type.
      const [groupsRes, debtsRes] = await Promise.all([
        api.get('/groups'),
        api.get(`/dashboard/debts-with/${otherUserId}`).catch(() => ({ data: { groups: [] } })),
      ]);
      const debtMap = {};
      (debtsRes.data?.groups || []).forEach((g) => { debtMap[g.group_id] = g; });
      setPayeeUpi(debtsRes.data?.user?.upi_id || null);
      const all = groupsRes.data || [];
      const shared = all
        .filter((g) => (g.member_ids || []).includes(otherUserId))
        .map((g) => ({ ...g, debt: debtMap[g.id] || { owe: 0, owed: 0 } }));
      setSharedGroups(shared);
      // Pre-select the group with the largest "I owe" amount so the user can
      // tap the chip + Settle in two taps.
      const top = [...shared].sort((a, b) => (b.debt.owe || 0) - (a.debt.owe || 0))[0];
      if (top) {
        setSelectedGroupId(top.id);
        if (top.debt.owe > 0) setAmountInput(String(top.debt.owe));
      }
    } catch {
      setSharedGroups([]);
    }
  }, []);

  // Start camera when entering scanning state.
  useEffect(() => {
    if (!open || state !== 'scanning') return;
    let cancelled = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (cancelled) return;
        // Give the DOM a tick to mount the container.
        await new Promise((r) => setTimeout(r, 80));
        if (cancelled) return;
        const region = document.getElementById(SCANNER_ID);
        if (!region) return;
        const inst = new Html5Qrcode(SCANNER_ID, { verbose: false });
        html5QrRef.current = inst;
        await inst.start(
          { facingMode: 'environment' },
          { fps: 12, qrbox: { width: 220, height: 220 } },
          (decodedText) => {
            const parsed = parsePayload(decodedText);
            if (!parsed) return; // ignore non-SplitEasy QRs silently
            // First valid match wins — stop camera and switch to review.
            setDecoded(parsed);
            // For per-debt QR (settle), amount + group come from payload.
            // For personal QR (user), we need to fetch shared groups and let
            // the payer pick a group + amount.
            if (parsed._kind === 'settle') {
              setAmountInput(parsed.amount ? String(parsed.amount) : '');
              // iter235 — fetch the payee's UPI ID for the deep-link button
              api.get(`/dashboard/debts-with/${parsed.to_user_id}`)
                .then((r) => setPayeeUpi(r.data?.user?.upi_id || null))
                .catch(() => setPayeeUpi(null));
            } else if (parsed._kind === 'user') {
              setAmountInput('');
              loadSharedGroups(parsed.user_id);
            }
            setState('review');
            stopScanner();
          },
          () => { /* per-frame failures are normal — ignore */ },
        );
      } catch (e) {
        if (cancelled) return;
        setError(
          e?.message?.includes('Permission')
            ? 'Camera permission denied. Enable it in browser settings to scan.'
            : 'Camera unavailable on this device.'
        );
        setState('error');
      }
    })();
    return () => { cancelled = true; stopScanner(); };
  }, [open, state, stopScanner]);

  const handleConfirm = async () => {
    if (!decoded) return;
    const amt = Number(amountInput);
    if (!amt || amt <= 0) {
      toast.error('Enter a positive amount');
      return;
    }
    // Resolve group + recipient based on QR kind.
    const groupId = decoded._kind === 'settle' ? decoded.group_id : selectedGroupId;
    const toUserId = decoded._kind === 'settle' ? decoded.to_user_id : decoded.user_id;
    if (!groupId) {
      toast.error('Pick a group to settle in');
      return;
    }
    setState('sending');
    try {
      const res = await api.post('/coin-transfers/qr-settle', {
        group_id: groupId,
        to_user_id: toUserId,
        amount: amt,
        note: decoded.note || undefined,
      });
      toast.success(res.data?.message || 'Settled');
      onSettled?.(res.data?.settlement);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to settle';
      toast.error(detail);
      setState('review');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl" data-testid="qr-scan-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-xl inline-flex items-center gap-2">
            <Camera className="w-5 h-5" />
            Settle by QR
          </SheetTitle>
          <SheetDescription>
            Point your camera at your friend's SplitEasy QR to clear what you owe them.
            This is between the two of you — <span className="font-semibold">the group fund is never touched</span>.
          </SheetDescription>
        </SheetHeader>

        <div className="py-4 min-h-[320px]">
          {state === 'scanning' && (
            <div className="space-y-3">
              <div
                id={SCANNER_ID}
                className="w-full aspect-square max-w-xs mx-auto rounded-2xl overflow-hidden bg-black"
                data-testid="qr-scan-region"
              />
              <p className="text-xs text-center text-muted-foreground">
                Tip — hold steady, good light, fill the box
              </p>
            </div>
          )}

          {state === 'review' && decoded && (
            <div className="space-y-4" data-testid="qr-scan-review">
              <Card className="p-4 rounded-2xl border-2 border-emerald-500/40 bg-emerald-50/40 dark:bg-emerald-950/15">
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">
                  You're settling with
                </p>
                <p className="font-display text-2xl font-bold mt-1">
                  {decoded._kind === 'settle' ? decoded.to_name : decoded.name}
                </p>
                {decoded.note && (
                  <p className="text-xs text-muted-foreground italic mt-1">"{decoded.note}"</p>
                )}
              </Card>

              {/* iter120 — group picker for personal QRs */}
              {decoded._kind === 'user' && (
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1.5 block inline-flex items-center gap-1">
                    <Users className="w-3 h-3" /> Which group?
                  </label>
                  {sharedGroups.length === 0 ? (
                    <div className="p-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-xs text-amber-700 dark:text-amber-300" data-testid="qr-scan-no-shared">
                      You're not in any group with {decoded.name}. Join one of their groups first, then try again.
                    </div>
                  ) : (
                    <Select value={selectedGroupId} onValueChange={(v) => {
                      setSelectedGroupId(v);
                      // iter121 — when group flips, snap amount to the debt for that group.
                      const g = sharedGroups.find((x) => x.id === v);
                      if (g?.debt?.owe > 0) setAmountInput(String(g.debt.owe));
                    }}>
                      <SelectTrigger className="h-11 rounded-xl" data-testid="qr-scan-group-select">
                        <SelectValue placeholder="Pick a shared group" />
                      </SelectTrigger>
                      <SelectContent>
                        {sharedGroups.map((g) => (
                          <SelectItem key={g.id} value={g.id}>
                            {g.name}
                            {g.debt?.owe > 0 && (
                              <span className="ml-2 text-xs text-rose-600 dark:text-rose-300 font-bold">
                                · owe {formatNXC(g.debt.owe)}
                              </span>
                            )}
                            {g.debt?.owed > 0 && (
                              <span className="ml-2 text-xs text-emerald-600 dark:text-emerald-300 font-bold">
                                · owed {formatNXC(g.debt.owed)}
                              </span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {/* iter121 — auto-fill chips: tap to instantly set amount */}
                  {selectedGroupId && (() => {
                    const g = sharedGroups.find((x) => x.id === selectedGroupId);
                    const owe = g?.debt?.owe || 0;
                    if (owe <= 0) return null;
                    return (
                      <div className="mt-2 flex flex-wrap gap-1.5" data-testid="qr-scan-debt-chips">
                        <button
                          type="button"
                          onClick={() => setAmountInput(String(owe))}
                          className="text-xs px-2.5 py-1 rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300 font-bold hover:bg-rose-500/25 transition-colors"
                          data-testid={`debt-chip-full-${selectedGroupId}`}
                        >
                          Full owed · {formatNXC(owe)}
                        </button>
                        {owe >= 200 && (
                          <button
                            type="button"
                            onClick={() => setAmountInput(String(Math.round(owe / 2)))}
                            className="text-xs px-2.5 py-1 rounded-full bg-muted text-foreground/80 font-semibold hover:bg-muted/70 transition-colors"
                            data-testid={`debt-chip-half-${selectedGroupId}`}
                          >
                            Half · {formatNXC(Math.round(owe / 2))}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-1.5 block">
                  Amount (₹)
                </label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  inputMode="numeric"
                  value={amountInput}
                  onChange={(e) => setAmountInput(e.target.value)}
                  placeholder="0"
                  className="text-2xl font-display font-bold tabular-nums h-14 text-center"
                  data-testid="qr-scan-amount-input"
                  autoFocus={decoded._kind === 'user' || !decoded.amount}
                  readOnly={decoded._kind === 'settle' && !!decoded.amount}
                />
                {decoded._kind === 'settle' && decoded.amount > 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Amount locked by QR — to change it, ask the receiver to regenerate.
                  </p>
                )}
              </div>

              {amountInput && Number(amountInput) > 0 && (
                <div className="p-3 rounded-xl bg-muted/50 text-center">
                  <p className="text-xs text-muted-foreground">After settling</p>
                  <p className="text-sm font-semibold">
                    Your balance with{' '}
                    <span className="font-bold">
                      {decoded._kind === 'settle' ? decoded.to_name : decoded.name}
                    </span>{' '}
                    drops by{' '}
                    <span className="text-rose-600 dark:text-rose-300 font-bold tabular-nums">
                      {formatNXC(Number(amountInput))}
                    </span>
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={() => {
                    setDecoded(null);
                    setSharedGroups([]);
                    setSelectedGroupId('');
                    setState('scanning');
                  }}
                  data-testid="qr-scan-rescan"
                >
                  <X className="w-4 h-4 mr-1" /> Rescan
                </Button>
                <Button
                  className="flex-1 rounded-full"
                  onClick={handleConfirm}
                  disabled={
                    !amountInput ||
                    Number(amountInput) <= 0 ||
                    (decoded._kind === 'user' && !selectedGroupId)
                  }
                  data-testid="qr-scan-confirm"
                >
                  <Check className="w-4 h-4 mr-1" /> Settle now
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {/* iter235 — real-money UPI deep-link when payee saved a UPI ID */}
              {payeeUpi && Number(amountInput) > 0
                && (decoded._kind === 'settle' || selectedGroupId) && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs uppercase tracking-wider text-muted-foreground font-bold">or</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  <UpiPayButton
                    upiId={payeeUpi}
                    payeeName={decoded._kind === 'settle' ? decoded.to_name : decoded.name}
                    amount={Number(amountInput)}
                    note={`SplitEasy: ${(decoded._kind === 'user'
                      ? sharedGroups.find((g) => g.id === selectedGroupId)?.name
                      : decoded.group_name) || 'settle'}`}
                    onConfirmed={handleConfirm}
                  />
                </>
              )}
            </div>
          )}

          {state === 'sending' && (
            <div className="flex flex-col items-center justify-center min-h-[260px] gap-3" data-testid="qr-scan-sending">
              <div className="w-12 h-12 rounded-full border-4 border-primary border-t-transparent animate-spin" />
              <p className="text-sm text-muted-foreground">Updating ledgers…</p>
            </div>
          )}

          {state === 'error' && (
            <div className="flex flex-col items-center justify-center min-h-[260px] gap-3" data-testid="qr-scan-error">
              <AlertTriangle className="w-10 h-10 text-amber-500" />
              <p className="text-sm text-center text-muted-foreground max-w-xs">{error}</p>
              <Button
                variant="outline"
                onClick={() => { setError(null); setState('scanning'); }}
                className="rounded-full"
              >
                Try again
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
